import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, StatusChangeEvent, Validators } from '@angular/forms';
import { BxInputComponent, BxTagAction, BxTagComponent, injectBxIcons, plusIcon } from '@beyonnex/bx-angular-ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { Thing } from '../../../../models/thing.models';
import { DittoService } from '../../../../services/ditto/ditto.service';
import { CustomValidators } from '../../../../utils/form/form.validators';
import { DeviceDetailsStore } from '../../device-details.store';

const MAX_LENGTH = 50;
const UNREMOVABLE_TAGS = ['refurbished'];

@Component({
  selector: 'bxi-device-tags',
  imports: [BxTagComponent, TranslatePipe, BxInputComponent, ReactiveFormsModule],
  templateUrl: './device-tags.component.html',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeviceTagsComponent {
  private readonly translate = inject(TranslateService);
  private readonly ditto = inject(DittoService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly store = inject(DeviceDetailsStore);
  protected readonly device = toSignal(this.store.device$);
  protected readonly tags = computed<{ label: string; action: BxTagAction }[]>(
    () =>
      this.device()?.attributes?.tags?.map((tag) => ({
        label: tag,
        action: !UNREMOVABLE_TAGS.includes(tag.toLowerCase()) ? 'x-close' : 'text-only',
      })) || [],
  );

  protected readonly tagForm = this.formBuilder.nonNullable.group({
    tag: ['', [Validators.maxLength(MAX_LENGTH), CustomValidators.noWhitespace, Validators.pattern('[a-zA-Z]*')]],
  });

  private readonly statusChangeEvents = toSignal(
    this.tagForm.get('tag')!.events.pipe(filter((event): event is StatusChangeEvent => event instanceof StatusChangeEvent)),
  );
  protected readonly hintText = computed(() => {
    const event = this.statusChangeEvents();
    if (event?.source.errors?.['whitespace']) {
      return this.translate.instant('system.validation.whitespace');
    }

    if (event?.source.errors?.['maxlength'] || event?.source.errors?.['pattern']) {
      return this.translate.instant('tags.validation.tagFormat', { length: MAX_LENGTH });
    }

    return '';
  });
  protected readonly isError = computed(() => {
    const event = this.statusChangeEvents();
    if (event?.source.errors?.['whitespace']) {
      return true;
    }

    return false;
  });

  constructor() {
    injectBxIcons([plusIcon]);
  }

  protected handleRemove(tag: string): void {
    const thingId = this.device()?.thingId;
    const currentTags = this.device()?.attributes?.tags;
    if (thingId && currentTags?.length) {
      const newTags = currentTags.filter((t) => t !== tag);
      this.ditto.updateThing(thingId, Thing.propertyPath('attributes.tags', '/'), newTags.length ? newTags : undefined).subscribe();
    }
  }

  protected handleAddTag(): void {
    if (this.isError()) {
      return;
    }

    const tagControl = this.tagForm.get('tag');
    const thingId = this.device()?.thingId;
    if (tagControl?.value && thingId) {
      const currentTags = this.device()?.attributes?.tags || [];
      this.ditto.updateThing(thingId, Thing.propertyPath('attributes.tags', '/'), [...currentTags, tagControl.value]).subscribe();
      tagControl.reset();
    }
  }
}
